/**
 * AppShell.tsx — Layout base da aplicação
 *
 * Barra de navegação superior + container de conteúdo.
 * Exibe usuário logado, links de navegação e botão de logout.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../lib/auth'

interface AppShellProps {
  children: ReactNode
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  )
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [breakpoint])
  return isMobile
}

export function AppShell({ children }: AppShellProps) {
  const { user, role, signOut } = useAuth()
  const location = useLocation()
  const isMobile = useIsMobile()
  const [menuOpen, setMenuOpen] = useState(false)

  // Fecha menu ao mudar de rota
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  // Não exibe navbar na página de login
  if (location.pathname === '/login') {
    return <>{children}</>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Navbar */}
      <nav
        style={{
          height: isMobile ? '48px' : '44px',
          background: 'rgba(8, 8, 16, 0.95)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '0 12px' : '0 20px',
          gap: isMobile ? '12px' : '24px',
          backdropFilter: 'blur(10px)',
          flexShrink: 0,
          zIndex: 100,
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          style={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#57f5c3',
            letterSpacing: '0.05em',
            textDecoration: 'none',
          }}
        >
          AI BAM
        </Link>

        {/* Links — desktop inline / mobile drawer */}
        {!isMobile ? (
          <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
            <NavLink to="/" label="Grafo" active={location.pathname === '/'} />
            <NavLink to="/search" label="Busca" active={location.pathname === '/search'} />
            {role === 'admin' && (
              <>
                <NavLink to="/admin" label="Admin" active={location.pathname === '/admin'} />
                <NavLink to="/logs" label="Logs" active={location.pathname === '/logs'} />
              </>
            )}
          </div>
        ) : (
          <div style={{ flex: 1 }} />
        )}

        {/* Usuário (desktop full / mobile só admin badge + botão menu) */}
        {user && !isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '12px', color: '#5a5a80' }}>
              {user.email}
            </span>
            {role === 'admin' && (
              <span
                style={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  background: 'rgba(87, 245, 195, 0.15)',
                  color: '#57f5c3',
                  border: '1px solid rgba(87, 245, 195, 0.3)',
                }}
              >
                admin
              </span>
            )}
            <button
              onClick={signOut}
              className="btn"
              style={{ padding: '3px 10px', fontSize: '11px' }}
            >
              sair
            </button>
          </div>
        )}

        {/* Botão hamburger (mobile) */}
        {isMobile && user && (
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
            style={{
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '6px',
              color: '#cfd0e0',
              cursor: 'pointer',
              padding: 0,
              fontSize: '18px',
            }}
          >
            {menuOpen ? '×' : '☰'}
          </button>
        )}
      </nav>

      {/* Drawer mobile */}
      {isMobile && menuOpen && (
        <>
          <div
            onClick={() => setMenuOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 200,
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: '48px',
              right: 0,
              width: '78%',
              maxWidth: '320px',
              height: 'calc(100vh - 48px)',
              background: 'rgba(13, 13, 30, 0.98)',
              borderLeft: '1px solid rgba(255,255,255,0.08)',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              zIndex: 201,
              backdropFilter: 'blur(20px)',
            }}
          >
            <div style={{ fontSize: '11px', color: '#5a5a80', marginBottom: '4px', wordBreak: 'break-all' }}>
              {user?.email}
              {role === 'admin' && (
                <span style={{ marginLeft: '8px', color: '#57f5c3' }}>(admin)</span>
              )}
            </div>
            <NavLink to="/" label="Grafo" active={location.pathname === '/'} block />
            <NavLink to="/search" label="Busca" active={location.pathname === '/search'} block />
            {role === 'admin' && (
              <>
                <NavLink to="/admin" label="Admin" active={location.pathname === '/admin'} block />
                <NavLink to="/logs" label="Logs" active={location.pathname === '/logs'} block />
              </>
            )}
            <div style={{ flex: 1 }} />
            <button
              onClick={() => { setMenuOpen(false); signOut() }}
              className="btn"
              style={{ padding: '8px 12px', fontSize: '13px', justifyContent: 'center' }}
            >
              sair
            </button>
          </div>
        </>
      )}

      {/* Conteúdo */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {children}
      </div>
    </div>
  )
}

function NavLink({
  to,
  label,
  active,
  block,
}: {
  to: string
  label: string
  active: boolean
  block?: boolean
}) {
  return (
    <Link
      to={to}
      style={{
        padding: block ? '10px 12px' : '4px 12px',
        borderRadius: '5px',
        fontSize: block ? '14px' : '13px',
        textDecoration: 'none',
        color: active ? '#57f5c3' : '#9090b0',
        background: active ? 'rgba(87, 245, 195, 0.1)' : 'transparent',
        border: active ? '1px solid rgba(87, 245, 195, 0.2)' : '1px solid transparent',
        transition: 'all 150ms ease',
        display: block ? 'block' : 'inline-flex',
      }}
    >
      {label}
    </Link>
  )
}
